import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { activity } from '../../app/activity'
import { timeCursor } from '../../app/time/cursor'
import { skyBootPaint } from '../sky/boot'
import type { SkyController } from '../sky/types'
import { frameAtCursor } from './bindings'
import { afterPaint } from './afterPaint'
import type { V5Data } from '../data/types'
import '../sky/layer.css'

const LIVING_SKY = false

function shouldPause(obscured: boolean): boolean {
  const a = activity.getSnapshot()
  return document.hidden || obscured || a.scrolling || a.sheetOpen || a.idle
}

export function SkyLayer({ data, reduced, obscured }: { data: V5Data; reduced: boolean; obscured: boolean }) {
  const canvas = useRef<HTMLCanvasElement>(null)
  const plate = useRef<HTMLDivElement>(null)
  const controller = useRef<SkyController | null>(null)
  const importing = useRef(false)
  const generation = useRef(0)
  const flags = useRef({ reduced, obscured })
  flags.current = { reduced, obscured }
  const frame = frameAtCursor(data)
  const first = frame?.sky
  const [bootStyle] = useState(() => ({ backgroundImage: first ? skyBootPaint(first) : 'none' }))
  const latest = useRef(first)
  latest.current = first
  useEffect(() => {
    const node = canvas.current
    // The still gradient carries the sky; the WebGL layer cost frames under scrolling cards.
    if (!LIVING_SKY || !node || controller.current || importing.current || !latest.current) return
    const token = ++generation.current
    importing.current = true
    // Shader/material work starts after the complete text and matching still have painted.
    const cancel = afterPaint(() => {
      void import('../sky/createSky').then(({ createSky }) => {
        if (token !== generation.current || node !== canvas.current || !latest.current) return
        const options = flags.current
        const sky = createSky(node, { state: latest.current, reducedMotion: options.reduced,
          onReady: () => node.setAttribute('data-ready', '') })
        controller.current = sky; importing.current = false
        if (shouldPause(options.obscured)) sky.pause()
      }).catch(() => { if (token === generation.current) importing.current = false })
    })
    return () => { cancel(); generation.current++; importing.current = false }
  }, [!!first])
  useLayoutEffect(() => () => {
    generation.current++; importing.current = false
    controller.current?.dispose(); controller.current = null
    canvas.current?.removeAttribute('data-ready')
  }, [])
  useEffect(() => { controller.current?.setReducedMotion(reduced) }, [reduced])
  useLayoutEffect(() => {
    const update = () => {
      const current = frameAtCursor(data)
      if (!current) return
      const state = current.sky
      latest.current = state
      controller.current?.setState(state)
      if (plate.current && !canvas.current?.hasAttribute('data-ready')) plate.current.style.backgroundImage = skyBootPaint(state)
      if (timeCursor.isNow()) {
        try {
          const scene = data.mock ? `demo-${new URLSearchParams(location.search).get('mock') ?? 'partly'}-` : ''
          localStorage.setItem(`wf5-sky-${scene}${data.place.id}`, JSON.stringify({ night: current.night, condition: current.condition }))
        } catch { /* optional boot hint */ }
      }
    }
    update()
    return timeCursor.subscribe(update)
  }, [data])
  useEffect(() => {
    const sync = () => {
      if (shouldPause(obscured)) controller.current?.pause()
      else controller.current?.resume()
    }
    const unsubscribe = activity.subscribe(sync)
    const offCursor = timeCursor.subscribe(sync)
    document.addEventListener('visibilitychange', sync)
    sync()
    return () => { unsubscribe(); offCursor(); document.removeEventListener('visibilitychange', sync) }
  }, [obscured])
  return <div className="v5-sky sky" aria-hidden="true"><div className="v5-sky-plate" ref={plate} style={bootStyle} /><canvas ref={canvas} className="v5-sky-canvas" /><div className="v5-sky-veil" /></div>
}
